package com.tregz.tremenz.ui.media

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import org.khronos.webgl.Int8Array
import org.jetbrains.compose.resources.decodeToImageBitmap

private const val CACHE_LIMIT = 64

/** Decoded images by url, oldest first, so scrolling back does not refetch and redecode. */
private val imageCache = LinkedHashMap<String, ImageBitmap>()

private fun cached(url: String): ImageBitmap? {
    val hit = imageCache.remove(url) ?: return null
    imageCache[url] = hit // re-insert as most recently used
    return hit
}

private fun store(url: String, bitmap: ImageBitmap) {
    imageCache.remove(url)
    imageCache[url] = bitmap
    while (imageCache.size > CACHE_LIMIT) {
        imageCache.remove(imageCache.keys.first())
    }
}

@Composable
actual fun MediaImage(
    url: String?,
    contentDescription: String?,
    modifier: Modifier,
    contentScale: ContentScale
) {
    var bitmap by remember(url) { mutableStateOf(url?.let { cached(it) }) }

    LaunchedEffect(url) {
        if (url.isNullOrBlank()) {
            bitmap = null
            return@LaunchedEffect
        }
        if (bitmap != null) return@LaunchedEffect
        try {
            val response = window.fetch(url, js("{}")).await()
            if (!response.ok) {
                console.warn("Failed to fetch image from $url: HTTP ${response.status}")
                bitmap = null
                return@LaunchedEffect
            }
            val arrayBuffer = response.arrayBuffer().await()
            val bytes = Int8Array(arrayBuffer).unsafeCast<ByteArray>()
            bitmap = bytes.decodeToImageBitmap().also { store(url, it) }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("Error loading image from $url", e)
            bitmap = null
        }
    }

    val currentBitmap = bitmap
    if (currentBitmap != null) {
        Image(
            bitmap = currentBitmap,
            contentDescription = contentDescription,
            modifier = modifier,
            contentScale = contentScale
        )
    } else {
        Box(modifier = modifier.background(Color.DarkGray))
    }
}
